import type { VisualizationType } from '@aibi/contracts';
import { createVisualizationRegistry } from '@aibi/visualization-engine';
import type { HealthApi } from '../api/health-api';
import type { ReportApi } from '../report/report-api';
import type { AdminServices } from '../settings/services/admin-services';
import { createDefaultRendererRegistry } from '../visualization/registry';
import { createAICoreAssistant } from '../workspace/services/ai-core-assistant';
import type { AIRelayApi } from '../workspace/services/ai-relay-api';
import {
  createUnconfiguredAssistant,
  type AssistantPort,
} from '../workspace/services/assistant-port';
import { createCompositeModelCatalog } from '../workspace/services/composite-model-catalog';
import { createServerBackedAssistant } from '../workspace/services/server-backed-assistant';
import { DEMO_MODELS, demoPreviewMembers } from '../workspace/services/demo-models';
import { createPreviewQueryResultSource } from '../workspace/services/preview-query-result-source';
import type { QueryApi } from '../workspace/services/query-api';
import type { QueryResultSource } from '../workspace/services/query-result-source';
import { createRoutingQueryResultSource } from '../workspace/services/routing-query-result-source';
import {
  createInMemoryModelCatalog,
  type SemanticModelCatalog,
} from '../workspace/services/semantic-model-catalog';
import { createServerQueryResultSource } from '../workspace/services/server-query-result-source';
import {
  createLocalWorkspaceRepository,
  createMemoryWorkspaceRepository,
  type KeyValueStorage,
} from '../workspace/services/workspace-repository';
import type { AppServices } from './services';

export interface AppServicesOptions {
  readonly healthApi: HealthApi;
  /** Almacenamiento del borrador; sin el, el workspace vive solo en memoria. */
  readonly storage?: KeyValueStorage;
  /** Latencia simulada del preview para hacer visibles los estados de carga. */
  readonly previewLatencyMs?: number;
  /** Cliente del relay de IA del servidor; necesario para el modo `auto`. */
  readonly aiRelayApi?: AIRelayApi;
  /**
   * Cliente de la ejecucion real. Con el, los modelos del servidor se consultan en vivo y
   * los demo siguen en preview; sin el (pruebas), todo es preview.
   */
  readonly queryApi?: QueryApi;
  /**
   * Asistente: `auto` usa el proveedor del servidor si esta habilitado y, si no, el
   * interprete local; `local-rules` fuerza el interprete local (pruebas); `unconfigured`
   * desactiva el asistente. Por defecto, `auto` si hay `aiRelayApi` y `local-rules` si no.
   */
  readonly assistant?: 'auto' | 'local-rules' | 'unconfigured';
  /** Clientes de configuracion; sin ellos (escritorio sin servidor, pruebas) no se ofrece. */
  readonly admin?: AdminServices;
  /** Reportes publicados; sin ellos (escritorio sin servidor, pruebas) no se ofrece publicar. */
  readonly reports?: ReportApi;
}

/**
 * Compone los adaptadores de la aplicacion: modelos del servidor (ejecucion real) y demo
 * (preview), asistente sobre el AI Core en proceso con el proveedor del servidor (n8n) si
 * esta habilitado o el interprete local de reglas como respaldo.
 *
 * @example
 * const services = createAppServices({ healthApi, queryApi, storage: window.localStorage });
 */
export function createAppServices(options: AppServicesOptions): AppServices {
  const demoCatalog = createInMemoryModelCatalog(DEMO_MODELS);
  const preview = createPreviewQueryResultSource({
    members: (dimensionId) => demoPreviewMembers[dimensionId],
    ...(options.previewLatencyMs === undefined ? {} : { latencyMs: options.previewLatencyMs }),
  });
  let modelCatalog: SemanticModelCatalog = demoCatalog;
  let queryResultSource: QueryResultSource = preview;
  if (options.queryApi) {
    const composite = createCompositeModelCatalog({ demo: demoCatalog, api: options.queryApi });
    modelCatalog = composite;
    queryResultSource = createRoutingQueryResultSource({
      isLive: (modelId) => composite.isLive(modelId),
      live: createServerQueryResultSource({ api: options.queryApi, modelCatalog: composite }),
      preview,
    });
  }

  const visualizationCatalog = createVisualizationRegistry();
  const rendererRegistry = createDefaultRendererRegistry();
  const assistantOptions = {
    modelCatalog,
    visualizations: () => visualizationCatalog.list(),
    isRenderable: (type: VisualizationType) => rendererRegistry.has(type),
    members: demoPreviewMembers,
  };
  const mode = options.assistant ?? (options.aiRelayApi ? 'auto' : 'local-rules');
  const assistant: AssistantPort =
    mode === 'unconfigured'
      ? createUnconfiguredAssistant()
      : mode === 'auto' && options.aiRelayApi
        ? createServerBackedAssistant({ ...assistantOptions, relayApi: options.aiRelayApi })
        : createAICoreAssistant(assistantOptions);
  return {
    healthApi: options.healthApi,
    modelCatalog,
    queryResultSource,
    assistant,
    workspaceRepository: options.storage
      ? createLocalWorkspaceRepository(options.storage)
      : createMemoryWorkspaceRepository(),
    visualizationCatalog,
    rendererRegistry,
    ...(options.admin === undefined ? {} : { admin: options.admin }),
    ...(options.reports === undefined ? {} : { reports: options.reports }),
  };
}
